import type { Difficulty, FillQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-5：除以一位數，無餘數（整除）。
 * 三上第七章：除法初步，只學整除，有餘數留三下。
 *
 * difficulty 1：商 1–9，除數 2–5（被除數 ≤ 45）
 * difficulty 2：商 10–19，除數 2–9（被除數 20–171）
 * difficulty 3：被除數二位數÷一位數，商為兩位（被除數 20–99）
 */
export function generateDivNoRem(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);

  let dividend: number;
  let divisor: number;
  let quotient: number;

  if (difficulty === 1) {
    divisor = randInt(rand, 2, 5);
    quotient = randInt(rand, 1, 9);
    dividend = divisor * quotient;
  } else if (difficulty === 2) {
    divisor = randInt(rand, 2, 9);
    quotient = randInt(rand, 10, 19);
    dividend = divisor * quotient;
  } else {
    // d3: two-digit dividend, one-digit divisor, exact division
    divisor = randInt(rand, 2, 9);
    // Pick quotient so dividend is in 20..99
    const qMin = Math.ceil(20 / divisor);
    const qMax = Math.floor(99 / divisor);
    quotient = randInt(rand, Math.max(qMin, 2), Math.max(qMax, 2));
    dividend = divisor * quotient;
  }

  return {
    kind: 'fill',
    prompt: `${dividend} ÷ ${divisor} = ?`,
    correctAnswer: String(quotient),
    acceptedAnswers: [String(quotient)],
    explanation: `${divisor} × ${quotient} = ${dividend}，所以 ${dividend} ÷ ${divisor} = ${quotient}（整除，無餘數）。`,
    kp: 'math.div.1digit.no-rem',
  };
}
